/**
 * Temaregistret — enda källan till vilka teman som finns.
 *
 * Varje tema är ett block med CSS-variabler (se themes/*.css). Registret här
 * håller metadatan som CSS inte kan uttrycka: etikett, gruppering i väljaren,
 * polaritet och diagrampalett.
 *
 * Förhandsvisningsfärger står med flit INTE här. Temaväljaren sätter
 * data-theme på förhandsvisningen och läser temats riktiga tokens, så
 * kopierade hexvärden som kan glida isär behövs inte.
 *
 * POLARITET är inte kosmetik. Regler som bara gäller ljusa teman — det mörka
 * chippet bakom flair, vända kartscrims — är keyade på `data-polarity`, inte
 * på temanamnet. Annars hade varje nytt ljust tema (Office, Mushroom Kingdom,
 * Lemmings) missat dem allihop.
 *
 * MÖRKT ÄR SPECIALFALLET: mörka temats variabler bor i `:root` i App.css och
 * har inget eget attributblock. Det är facit — rör det aldrig. Alla andra
 * teman är overrides ovanpå.
 *
 * ÄGANDE: mörkt och ljust är gratis, övriga teman KÖPS i tipsbutiken. Vilka
 * spelaren äger avgörs av servern (`owned_themes` i /api/auth/me) — registret
 * här vet bara vilka teman som finns, aldrig vem som får använda dem.
 *
 * Registret och Djangos katalog (backend/src/players/themes.py) är två listor i
 * två repon och kan glida isär. Det är hanterat och inte förbjudet: väljaren
 * renderar snittet. Ett tema som saknas här har ingen palett att visa, och ett
 * som saknas där går inte att spara. Ett halvlandat tema syns alltså inte alls.
 *
 * Lägga till ett tema:
 *   1. themes/<grupp>/<id>.css med ett `[data-theme="<id>"]`-block
 *   2. importera filen i themes/index.css
 *   3. lägg till posten i themes/catalog.mjs (datan bor där, inte här)
 *   4. kör temavalidatorn — kontrast och diagrampalett måste passera
 *   5. sätt `released=True` i backendkatalogen och kör `seed_theme_items`
 *      så temat blir köpbart
 */

import { THEME_BY_ID, THEME_GROUPS, THEMES, POLARITY_DARK } from './catalog.mjs';

export {
  POLARITY_DARK,
  POLARITY_LIGHT,
  THEME_GROUPS,
  THEMES,
  THEME_BY_ID,
} from './catalog.mjs';

/** Temat som gäller när inget är valt eller det valda inte känns igen. */
export const DEFAULT_THEME_ID = 'dark';

/**
 * Teman alla får använda utan att äga något.
 *
 * Speglar FREE_THEME_IDS i backendkatalogen och används som fallback när
 * användaren är utloggad. Facit för en inloggad spelare är alltid serverns
 * `owned_themes` — den här listan är bara startvärdet.
 */
export const FREE_THEME_IDS = ['dark', 'light'];

/**
 * Polariteten för ett tema-id, med mörkt som fallback.
 *
 * Ett okänt id kan komma från en gammal localStorage-cache eller ett tema som
 * tagits bort. Mörkt är rätt fallback eftersom det bor i `:root` och alltid
 * renderar.
 *
 * @param {string} themeId
 * @returns {string} 'dark' eller 'light'
 */
export function polarityOf(themeId) {
  return THEME_BY_ID[themeId]?.polarity ?? POLARITY_DARK;
}

/**
 * Laddar temats typsnitt om det behöver ett eget.
 *
 * App.css importerar Rajdhani + Orbitron blockerande. Att lägga wildcard-
 * temanas display-typsnitt i samma import hade gjort varje sidladdning
 * långsammare för alla, för en handfull temans skull. I stället injiceras
 * en <link> första gången ett sådant tema aktiveras.
 *
 * Idempotent — samma URL laddas aldrig två gånger.
 *
 * @param {string} themeId
 */
export function ensureThemeFont(themeId) {
  const url = THEME_BY_ID[themeId]?.fontUrl;
  if (!url) return;
  if (document.querySelector(`link[data-theme-font="${themeId}"]`)) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.dataset.themeFont = themeId;
  document.head.appendChild(link);
}

/**
 * Teman grupperade för väljaren, i den ordning grupperna ska visas.
 *
 * @returns {Array<{group: string, themes: Array<object>}>}
 */
export function groupedThemes() {
  return THEME_GROUPS.map((group) => ({
    group,
    themes: THEMES.filter((t) => t.group === group),
  })).filter((g) => g.themes.length > 0);
}
